import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getWebSocketUrl } from '../../../config/websocket';
import {
  listProjectInsightsMessages,
  postProjectInsightsMessage,
  type SelaChatCitation,
  type SelaChatMessage,
} from '../../../services/selaApi';
import { websocketService, type WebSocketMessage } from '../../../services/websocketService';
import { recordV2ErrorStage, recordV2FunnelStep, recordV2Latency } from '../../../utils/v2Rum';
import type { AskSelaFocus } from './AskSelaContext';

const POLL_INTERVAL_MS = 3000;

export type AskSelaTurn = {
  key: string;
  user: SelaChatMessage | null;
  assistant: SelaChatMessage | null;
  kind: 'overview' | 'question' | 'refresh';
  createdAt: string;
};

type StreamingState = Record<string, { content: string; citations?: SelaChatCitation[] }>;

function mergeMessages(previous: SelaChatMessage[], incoming: SelaChatMessage[]): SelaChatMessage[] {
  if (!incoming.length) {
    return previous;
  }
  const byId = new Map<string, SelaChatMessage>();
  for (const message of previous) {
    if (message?.messageId) byId.set(message.messageId, message);
  }
  for (const message of incoming) {
    if (!message?.messageId) continue;
    const existing = byId.get(message.messageId);
    if (existing && existing.status === 'complete' && message.status === 'pending') {
      byId.set(message.messageId, {
        ...message,
        content: existing.content || message.content,
        status: existing.status,
        citations: existing.citations?.length ? existing.citations : message.citations,
        decisionBrief: existing.decisionBrief || message.decisionBrief,
        errorMessage: existing.errorMessage || message.errorMessage,
        agentMetadata: {
          ...(message.agentMetadata || {}),
          ...(existing.agentMetadata || {}),
        },
      });
    } else if (existing && !message.content && existing.content) {
      byId.set(message.messageId, {
        ...message,
        content: existing.content,
        citations: message.citations?.length ? message.citations : existing.citations,
        decisionBrief: message.decisionBrief || existing.decisionBrief,
      });
    } else {
      byId.set(message.messageId, message);
    }
  }
  return Array.from(byId.values()).sort((a, b) => (a.messageId || '').localeCompare(b.messageId || ''));
}

function buildTurns(messages: SelaChatMessage[]): AskSelaTurn[] {
  const turns: AskSelaTurn[] = [];
  let pendingUser: SelaChatMessage | null = null;
  for (const message of messages) {
    if (message.role === 'user') {
      if (pendingUser) {
        turns.push({
          key: pendingUser.messageId,
          user: pendingUser,
          assistant: null,
          kind: pendingUser.kind,
          createdAt: pendingUser.createdAt || pendingUser.messageId,
        });
      }
      pendingUser = message;
      continue;
    }
    turns.push({
      key: message.messageId,
      user: pendingUser,
      assistant: message,
      kind: message.kind,
      createdAt: message.createdAt || message.messageId,
    });
    pendingUser = null;
  }
  if (pendingUser) {
    turns.push({
      key: pendingUser.messageId,
      user: pendingUser,
      assistant: null,
      kind: pendingUser.kind,
      createdAt: pendingUser.createdAt || pendingUser.messageId,
    });
  }
  return turns;
}

function shouldPoll(messages: SelaChatMessage[], wsConnectionId: string | null, intelKickoffInProgress: boolean): boolean {
  if (intelKickoffInProgress) return true;
  const pending = messages.some((message) => message.status === 'pending');
  if (!pending) return false;
  if (messages.some((message) => message.status === 'pending' && message.kind === 'overview')) return true;
  return !wsConnectionId;
}

function parseWebSocketRoute(url: string): { domain: string; stage: string } {
  try {
    const parsed = new URL(url);
    const pathStage = parsed.pathname.replace(/^\//, '').split('/').filter(Boolean)[0] || '';
    return { domain: parsed.host, stage: pathStage };
  } catch {
    return { domain: '', stage: '' };
  }
}

export function useAskSelaChat(projectId: string, intelKickoffInProgress = false) {
  const [messages, setMessages] = useState<SelaChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState<StreamingState>({});
  const [wsConnectionId, setWsConnectionId] = useState<string | null>(websocketService.getConnectionId());
  const streamingRef = useRef<StreamingState>({});

  useEffect(() => {
    streamingRef.current = streaming;
  }, [streaming]);

  const refresh = useCallback(
    async (options: { showLoading?: boolean } = {}) => {
      try {
        if (options.showLoading) setLoading(true);
        const fresh = await listProjectInsightsMessages(projectId);
        setMessages((prev) => mergeMessages(prev, fresh));
        setLoadError(null);
      } catch (err) {
        if (options.showLoading) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load Ask Sela messages.');
        }
      } finally {
        if (options.showLoading) setLoading(false);
      }
    },
    [projectId],
  );

  useEffect(() => {
    setMessages([]);
    setStreaming({});
    void refresh({ showLoading: true });
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await websocketService.connect();
        const id = await websocketService.requestConnectionId();
        if (!cancelled) setWsConnectionId(id);
      } catch {
        if (!cancelled) setWsConnectionId(null);
      }
    })();

    const textChunkHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      const data = (message as any).data;
      if (!targetId || typeof data !== 'string' || !data) return;
      setStreaming((prev) => ({
        ...prev,
        [targetId]: {
          ...prev[targetId],
          content: `${prev[targetId]?.content || ''}${data}`,
        },
      }));
    };

    const citationsHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      const citations = (message as any).citations as SelaChatCitation[] | undefined;
      if (!targetId || !Array.isArray(citations)) return;
      setStreaming((prev) => ({
        ...prev,
        [targetId]: {
          ...prev[targetId],
          content: prev[targetId]?.content || '',
          citations,
        },
      }));
    };

    const resultHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      if (!targetId) return;
      const content = typeof (message as any).content === 'string' ? (message as any).content : streamingRef.current[targetId]?.content || '';
      const citations = Array.isArray((message as any).citations) ? ((message as any).citations as SelaChatCitation[]) : streamingRef.current[targetId]?.citations || [];
      setMessages((prev) => mergeMessages(prev, [{
        messageId: targetId,
        projectId,
        role: 'assistant',
        kind: ((message as any).query_type === 'overview' ? 'overview' : 'question'),
        content,
        status: 'complete',
        citations,
        decisionBrief: (message as any).decisionBrief || null,
        agentMetadata: {
          agent: (message as any).agent,
          kind: (message as any).query_type,
          askContext: (message as any).askContext || undefined,
        },
      }]));
      setStreaming((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      void refresh();
    };

    const errorHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      if (!targetId) return;
      setMessages((prev) => mergeMessages(prev, [{
        messageId: targetId,
        projectId,
        role: 'assistant',
        kind: 'question',
        content: '',
        status: 'error',
        citations: [],
        errorMessage: typeof (message as any).error === 'string' ? (message as any).error : 'Ask Sela could not answer.',
      }]));
      void refresh();
    };

    websocketService.onMessage('text_chunk', textChunkHandler);
    websocketService.onMessage('citations', citationsHandler);
    websocketService.onMessage('result', resultHandler);
    websocketService.onMessage('error', errorHandler);

    return () => {
      cancelled = true;
      websocketService.offMessage('text_chunk', textChunkHandler);
      websocketService.offMessage('citations', citationsHandler);
      websocketService.offMessage('result', resultHandler);
      websocketService.offMessage('error', errorHandler);
    };
  }, [projectId, refresh]);

  useEffect(() => {
    if (!shouldPoll(messages, wsConnectionId, intelKickoffInProgress)) return;
    const interval = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [intelKickoffInProgress, messages, refresh, wsConnectionId]);

  const turns = useMemo(() => buildTurns(messages), [messages]);

  const submitQuestion = useCallback(
    async (question: string, focus?: AskSelaFocus) => {
      const trimmed = question.trim();
      if (!trimmed || submitting) return;
      const startedAt = performance.now();
      setSubmitting(true);
      setSubmitError(null);
      recordV2FunnelStep('ask_sela_submit', 'start', {
        source_page: focus?.sourcePage || 'overview',
        focus_type: focus?.type || 'project',
      }, projectId);
      let connectionId: string | null = wsConnectionId;
      if (!connectionId) {
        try {
          connectionId = await websocketService.requestConnectionId();
          if (connectionId) setWsConnectionId(connectionId);
        } catch {
          connectionId = null;
        }
      }
      const { domain, stage } = parseWebSocketRoute(getWebSocketUrl());
      try {
        const created = await postProjectInsightsMessage(projectId, trimmed, {
          connectionId: connectionId || null,
          wsDomain: connectionId ? domain : null,
          wsStage: connectionId ? stage : null,
          askContext: focus,
        });
        setMessages((prev) => mergeMessages(prev, created));
        const assistant = created.find((message) => message.role === 'assistant');
        if (assistant) {
          setStreaming((prev) => ({
            ...prev,
            [assistant.messageId]: { content: '' },
          }));
        }
        recordV2FunnelStep('ask_sela_submit', 'success', {
          source_page: focus?.sourcePage || 'overview',
          focus_type: focus?.type || 'project',
        }, projectId);
        recordV2Latency('ask_sela_submit', performance.now() - startedAt, {}, projectId);
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to send question.');
        recordV2FunnelStep('ask_sela_submit', 'failure', {
          source_page: focus?.sourcePage || 'overview',
          focus_type: focus?.type || 'project',
        }, projectId);
        recordV2ErrorStage('ask_sela_submit', err, {}, projectId);
      } finally {
        setSubmitting(false);
      }
    },
    [projectId, submitting, wsConnectionId],
  );

  return {
    turns,
    loading,
    loadError,
    submitting,
    submitError,
    streaming,
    wsConnectionId,
    refresh,
    submitQuestion,
  };
}
